// src/styles/theme.js
import { extendTheme } from '@chakra-ui/react';
import { colors } from './colors';

/**
 * Tema Nogle — identidade visual oficial (manual da marca).
 *
 *   Neon Green  #01E975  → tecnologia / energia / AÇÕES e destaques
 *   Dark Green  #001300  → solidez / confiança (hero do dark mode)
 *   Bege        #F2EDDA  → humanização (fundo do light / texto do dark)
 *
 * Toda a adaptação light/dark passa por SEMANTIC TOKENS (abaixo). Componentes
 * devem usar os tokens (`surface`, `textPrimary`, `borderPrimary`, `secondary`…)
 * em vez de cores fixas (`white`, `gray.700`, `#151719`), para que os dois modos
 * fiquem corretos automaticamente.
 *
 *   • Dark  (padrão) = fundo neutro near-black + cinza escuro (mesmo padrão das
 *                      outras telas Nogle/Monitoramento), texto branco, neon em destaque.
 *   • Light          = fundo bege #F2EDDA, texto verde-escuro, neon nas ações.
 *   • Sidebar        = cinza escuro (#1A1A1C) nos DOIS modos (igual às outras telas).
 */

const config = {
    initialColorMode: 'dark',
    useSystemColorMode: false,
};

const fonts = {
    heading: `'Plus Jakarta Sans', sans-serif`,
    body: `'Plus Jakarta Sans', sans-serif`,
};

// ─── Semantic Tokens (fonte única da verdade para light/dark) ──────────────────
//  { default: <light>, _dark: <dark> }
const semanticTokens = {
    colors: {
        // Fundos — DARK neutro (near-black, padrão das outras telas Nogle) / LIGHT bege
        background: { default: '#F2EDDA', _dark: '#0B0B0C' },   // app
        bgSecondary: { default: '#FFFFFF', _dark: '#111113' },   // fundo secundário
        surface: { default: '#FFFFFF', _dark: '#161719' },       // cards, header, painéis
        surfaceRaised: { default: '#F6F3E7', _dark: '#202024' }, // chips, addons, header de tabela
        surfaceHover: { default: 'rgba(0,19,0,0.05)', _dark: 'rgba(255,255,255,0.05)' },
        overlayBg: { default: 'rgba(0,19,0,0.45)', _dark: 'rgba(0,0,0,0.66)' },

        // Kanban (Pipeline)
        boardBg: { default: '#ECE7D4', _dark: '#111113' },
        columnBg: { default: '#F6F3E7', _dark: '#161719' },
        cardBg: { default: '#FFFFFF', _dark: '#1C1C20' },

        // Texto
        textPrimary: { default: '#001300', _dark: '#FFFFFF' },   // verde-escuro / branco
        textSecondary: { default: '#4A5A4A', _dark: '#A0A3A8' },
        textMuted: { default: '#6B746B', _dark: '#6B6E74' },

        // Bordas — no LIGHT a linha é mais escura de propósito (separa as áreas
        // claras entre si; sem isso a tela fica "tudo branco" e difícil de ler)
        borderPrimary: { default: '#B3AB93', _dark: '#27272B' },
        borderSubtle: { default: 'rgba(0,19,0,0.16)', _dark: 'rgba(255,255,255,0.07)' },

        // Marca
        primary: { default: '#001300', _dark: '#001300' },       // verde-escuro (texto sobre o neon)
        secondary: { default: '#01E975', _dark: '#01E975' },     // neon — destaque/ações (igual nos 2 modos)
        secondaryHover: { default: '#00C864', _dark: '#00C864' },
        secondarySubtle: { default: 'rgba(1,233,117,0.14)', _dark: 'rgba(1,233,117,0.10)' },

        // Estados
        danger: { default: '#E53E3E', _dark: '#FC8181' },
        warning: { default: '#B7791F', _dark: '#ECC94B' },
        info: { default: '#2B6CB0', _dark: '#63B3ED' },

        // Sidebar — cinza escuro (igual às outras telas) nos DOIS modos
        sidebarBg: { default: '#1A1A1C', _dark: '#1A1A1C' },
        sidebarBorder: { default: 'rgba(255,255,255,0.08)', _dark: 'rgba(255,255,255,0.07)' },
        sidebarText: { default: 'rgba(255,255,255,0.75)', _dark: 'rgba(255,255,255,0.72)' },
    },
};

// ─── Estilos globais (adaptam ao modo via props.colorMode) ─────────────────────
const styles = {
    global: (props) => {
        const dark = props.colorMode === 'dark';
        return {
            'html, body, #root': {
                bg: 'background',
                color: 'textPrimary',
            },
            body: {
                fontFamily: `'Plus Jakarta Sans', sans-serif`,
                fontSize: '14px',
                transition: 'background-color 0.2s ease, color 0.2s ease',
            },
            '::placeholder': { color: 'textMuted' },
            // Scrollbar adaptável
            '::-webkit-scrollbar': { width: '8px', height: '8px' },
            '::-webkit-scrollbar-track': { background: 'transparent' },
            '::-webkit-scrollbar-thumb': {
                background: dark ? '#27272B' : '#B3AB93',
                borderRadius: '10px',
            },
            '::-webkit-scrollbar-thumb:hover': { background: dark ? '#01E975' : '#00C864' },
            // Seleção de texto
            '::selection': { background: dark ? 'rgba(1,233,117,0.30)' : 'rgba(1,233,117,0.25)' },
            // Inputs nativos (date/time) herdam o esquema do tema
            "input[type='date'], input[type='time']": {
                colorScheme: dark ? 'dark' : 'light',
            },
            // Option tags color override for light/dark mode
            "option": {
                background: dark ? '#1A1A1A' : '#ffffff',
                color: dark ? '#ffffff' : '#000000',
            },
        };
    },
};

// ─── Overrides de componentes ──────────────────────────────────────────────────
const components = {
    Button: {
        baseStyle: { borderRadius: 'lg', fontWeight: 700 },
        variants: {
            // Botão de ação: fundo neon + texto verde-escuro (igual nos 2 modos)
            solid: {
                bg: 'secondary',
                color: 'primary',
                _hover: { bg: 'secondaryHover', _disabled: { bg: 'secondary' } },
                _active: { bg: 'secondaryHover' },
            },
            // CTA primário canônico (use `variant="primary"` em todas as ações principais)
            primary: {
                bg: 'secondary',
                color: 'primary',
                fontWeight: 800,
                _hover: { bg: 'secondaryHover', _disabled: { bg: 'secondary' } },
                _active: { bg: 'secondaryHover' },
            },
            // Ação verde discreta (ex.: Concluir, Gerar)
            softGreen: {
                bg: 'secondarySubtle',
                color: 'secondary',
                fontWeight: 800,
                _hover: { bg: 'secondarySubtle', filter: 'brightness(1.15)' },
            },
            // Ação destrutiva padrão
            danger: {
                bg: 'danger',
                color: 'white',
                fontWeight: 800,
                _hover: { bg: 'red.600', _disabled: { bg: 'danger' } },
                _active: { bg: 'red.600' },
            },
            ghost: {
                color: 'textSecondary',
                _hover: { bg: 'surfaceHover', color: 'textPrimary' },
            },
            outline: {
                borderColor: 'borderPrimary',
                color: 'textPrimary',
                _hover: { bg: 'surfaceHover' },
            },
        },
    },
    Input: {
        variants: {
            outline: {
                field: {
                    bg: 'surface',
                    color: 'textPrimary',
                    borderColor: 'borderPrimary',
                    _placeholder: { color: 'textMuted' },
                    _hover: { borderColor: 'textMuted' },
                    _focus: { borderColor: 'secondary', boxShadow: 'none' },
                },
            },
        },
        defaultProps: { variant: 'outline' },
    },
    Textarea: {
        variants: {
            outline: {
                bg: 'surface',
                color: 'textPrimary',
                borderColor: 'borderPrimary',
                _placeholder: { color: 'textMuted' },
                _hover: { borderColor: 'textMuted' },
                _focus: { borderColor: 'secondary', boxShadow: 'none' },
            },
        },
        defaultProps: { variant: 'outline' },
    },
    Select: {
        variants: {
            outline: {
                field: {
                    bg: 'surface',
                    color: 'textPrimary',
                    borderColor: 'borderPrimary',
                    _focus: { borderColor: 'secondary', boxShadow: 'none' },
                    "option": {
                        bg: 'surface',
                        color: 'textPrimary',
                    }
                },
            },
        },
        defaultProps: { variant: 'outline' },
    },
    Modal: {
        baseStyle: {
            overlay: { bg: 'overlayBg', backdropFilter: 'blur(4px)' },
            dialog: {
                bg: 'surface',
                color: 'textPrimary',
                border: '1px solid',
                borderColor: 'borderPrimary',
            },
        },
    },
    Drawer: {
        baseStyle: {
            dialog: { bg: 'surface', color: 'textPrimary' },
        },
    },
    Popover: {
        baseStyle: {
            content: {
                bg: 'surface',
                borderColor: 'borderPrimary',
                _focus: { boxShadow: 'none' },
            },
        },
    },
    Menu: {
        baseStyle: {
            list: { bg: 'surface', borderColor: 'borderPrimary', boxShadow: 'lg' },
            item: {
                bg: 'surface',
                color: 'textPrimary',
                _hover: { bg: 'surfaceHover' },
                _focus: { bg: 'surfaceHover' },
            },
        },
    },
    Tooltip: {
        baseStyle: {
            bg: 'surfaceRaised',
            color: 'textPrimary',
            borderRadius: 'md',
        },
    },
    // No LIGHT, badges/chips ganham um contorno de 1px na cor do próprio texto
    // (GANHO verde, PERDIDO vermelho, etc.) — sem isso os fundos tintados somem
    // no tema claro. No dark nada muda. `inset` não altera o tamanho do chip.
    Badge: {
        baseStyle: (props) => (
            props.colorMode === 'light' ? { boxShadow: 'inset 0 0 0 1px' } : {}
        ),
    },
    Table: {
        baseStyle: {
            th: { borderColor: 'borderPrimary', color: 'textMuted' },
            td: { borderColor: 'borderPrimary' },
        },
    },
    Heading: {
        baseStyle: { color: 'textPrimary' },
    },
    Divider: {
        baseStyle: { borderColor: 'borderPrimary', opacity: 1 },
    },
};

export default extendTheme({
    config,
    styles,
    colors,
    fonts,
    components,
    semanticTokens,
});
